// Phase 67: InstallmentsWidget — display installment plans.
//
// Two modes:
//   - dashboard: show all ACTIVE plans across all cards (compact list)
//   - card:      show all plans for one card (with full details + actions)
//
// Pure presentational. Receives plans + card map + computed progress as props.
// Reads `computeInstallmentProgress` and `formatMonthLabel` for derived data.

import React from 'react';
import { computeInstallmentProgress } from '../../lib/installments.js';
import { useI18n } from '../../i18n/index.jsx';

export default function InstallmentsWidget({
  plans = [],
  cardsByUuid = {},
  onMarkPaid,
  onMarkEnded,
  onRemove,
  onAddInstallment,
  empty,
  mode = 'dashboard',
}) {
  const { t } = useI18n();
  const today = new Date();
  const active = plans.filter(p => !p.deleted && !p.ended);

  if (plans.length === 0) {
    return (
      <div
        className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4"
        data-testid="installments-empty"
      >
        <div className="text-sm text-slate-500 dark:text-slate-400 text-center">
          {empty || t('installments.empty')}
        </div>
        {onAddInstallment && (
          <button
            onClick={onAddInstallment}
            data-testid="installments-empty-add"
            className="mt-2 w-full text-sm text-brand-600 dark:text-brand-400"
          >
            + {t('installments.add')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3"
      data-testid="installments-widget"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          💳 {t('installments.title')}
        </h3>
        {onAddInstallment && mode === 'card' && (
          <button
            onClick={onAddInstallment}
            data-testid="installments-add"
            className="text-xs text-brand-600 dark:text-brand-400"
          >
            + {t('installments.add')}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {active.map(plan => {
          const progress = computeInstallmentProgress(plan, today);
          const card = cardsByUuid[plan.credit_card_local_uuid];
          return (
            <div
              key={plan.local_uuid}
              className="p-2 bg-slate-50 dark:bg-slate-900 rounded"
              data-testid={`installment-row-${plan.local_uuid}`}
            >
              <div className="flex justify-between items-baseline">
                <div className="font-medium text-sm text-slate-700 dark:text-slate-200">
                  {plan.note || card?.name || 'Installment'}
                </div>
                <div className="text-sm font-mono font-semibold text-slate-700 dark:text-slate-200">
                  ${plan.monthly_amount.toFixed(2)}/月
                </div>
              </div>

              {mode === 'card' && card && (
                <div className="text-xs text-slate-500 mt-0.5">
                  {card.name}
                </div>
              )}

              {/* Progress bar */}
              <div className="mt-1.5 h-1.5 bg-slate-200 dark:bg-slate-700 rounded overflow-hidden">
                <div
                  className="h-full bg-brand-500"
                  style={{ width: `${(progress.periods_paid / plan.total_periods) * 100}%` }}
                />
              </div>

              <div className="flex justify-between items-center mt-1 text-xs text-slate-500">
                <span data-testid={`installment-progress-${plan.local_uuid}`}>
                  {t('installments.periods', {
                    paid: progress.periods_paid,
                    total: plan.total_periods,
                  })}
                </span>
                {progress.next_payment_month && (
                  <span data-testid={`installment-next-${plan.local_uuid}`}>
                    {t('installments.nextPayment')}: {progress.next_payment_month}
                  </span>
                )}
              </div>

              {/* Actions */}
              {mode === 'card' && (onMarkPaid || onMarkEnded || onRemove) && (
                <div className="flex gap-2 mt-2">
                  {onMarkPaid && !progress.is_complete && (
                    <button
                      onClick={() => onMarkPaid(plan)}
                      data-testid={`installment-mark-paid-${plan.local_uuid}`}
                      className="text-xs px-2 py-1 bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-200 rounded"
                    >
                      ✓ {t('installments.markPaid')}
                    </button>
                  )}
                  {onMarkEnded && (
                    <button
                      onClick={() => onMarkEnded(plan)}
                      data-testid={`installment-mark-ended-${plan.local_uuid}`}
                      className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700"
                    >
                      {t('installments.markEnded')}
                    </button>
                  )}
                  {onRemove && (
                    <button
                      onClick={() => onRemove(plan)}
                      data-testid={`installment-remove-${plan.local_uuid}`}
                      className="text-xs px-2 py-1 text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
